import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';
import AuthContext from './auth-context';
import { getDealerFavoriteIds, toggleDealerFavorite, snapshotFromCar } from '../services/dealerGarage';

/**
 * Which dealer-stock cars the signed-in user has saved, app-wide.
 *
 * Auction favourites ride on the car search results (`isFavorite` is computed
 * by the API per row). Dealer stock comes from the marketing backend, which
 * knows nothing about our users, so the saved set has to be fetched once from
 * our own API and overlaid on every grid and detail page client-side.
 */
const DealerFavoritesContext = createContext({
    ids: new Set(),
    ready: false,
    isFavorite: () => false,
    isPending: () => false,
    toggle: async () => false,
    refresh: () => {},
});

export const DealerFavoritesProvider = ({ children }) => {
    const authCtx = useContext(AuthContext);
    const { enqueueSnackbar } = useSnackbar();
    const [ids, setIds] = useState(() => new Set());
    const [pending, setPending] = useState(() => new Set());
    const [ready, setReady] = useState(false);
    const loadedForUser = useRef(null);

    const refresh = useCallback(async () => {
        if (!authCtx.isLoggedIn) return;
        try {
            const res = await getDealerFavoriteIds();
            if (res?.message === 'Success' && Array.isArray(res.data)) {
                setIds(new Set(res.data));
            }
        } catch (err) {
            // Leave whatever we had; the hearts just stay as they were.
        } finally {
            setReady(true);
        }
    }, [authCtx.isLoggedIn]);

    // Load once per signed-in user; clear on sign out.
    useEffect(() => {
        const userId = authCtx.userData?.userId || authCtx.userData?._id || null;
        if (!authCtx.isLoggedIn || !userId) {
            loadedForUser.current = null;
            setIds(new Set());
            setReady(false);
            return;
        }
        if (loadedForUser.current === userId) return;
        loadedForUser.current = userId;
        refresh();
    }, [authCtx.isLoggedIn, authCtx.userData, refresh]);

    const toggle = useCallback(async (car) => {
        const vehicleId = car && car._id;
        if (!vehicleId) return false;
        if (!authCtx.isLoggedIn) {
            enqueueSnackbar('Sign in to save vehicles to your garage.', { variant: 'info' });
            return false;
        }
        if (pending.has(vehicleId)) return ids.has(vehicleId);

        const wasFavorite = ids.has(vehicleId);
        // Optimistic: flip the heart now, put it back if the API disagrees.
        setPending((prev) => new Set(prev).add(vehicleId));
        setIds((prev) => {
            const next = new Set(prev);
            if (wasFavorite) next.delete(vehicleId); else next.add(vehicleId);
            return next;
        });

        try {
            const res = await toggleDealerFavorite({ vehicleId, vehicle: snapshotFromCar(car) });
            const isFavorite = res?.data?.isFavorite;
            if (typeof isFavorite !== 'boolean') {
                throw new Error(res?.message || 'Could not update favorites');
            }
            setIds((prev) => {
                const next = new Set(prev);
                if (isFavorite) next.add(vehicleId); else next.delete(vehicleId);
                return next;
            });
            enqueueSnackbar(isFavorite ? 'Saved to your garage' : 'Removed from your garage', {
                variant: 'success',
                autoHideDuration: 2000,
            });
            return isFavorite;
        } catch (err) {
            setIds((prev) => {
                const next = new Set(prev);
                if (wasFavorite) next.add(vehicleId); else next.delete(vehicleId);
                return next;
            });
            enqueueSnackbar(err?.message || 'Could not update favorites. Please try again.', { variant: 'error' });
            return wasFavorite;
        } finally {
            setPending((prev) => {
                const next = new Set(prev);
                next.delete(vehicleId);
                return next;
            });
        }
    }, [authCtx.isLoggedIn, ids, pending, enqueueSnackbar]);

    const value = useMemo(() => ({
        ids,
        ready,
        isFavorite: (vehicleId) => ids.has(vehicleId),
        isPending: (vehicleId) => pending.has(vehicleId),
        toggle,
        refresh,
    }), [ids, ready, pending, toggle, refresh]);

    return (
        <DealerFavoritesContext.Provider value={value}>
            {children}
        </DealerFavoritesContext.Provider>
    );
};

export const useDealerFavorites = () => useContext(DealerFavoritesContext);

export default DealerFavoritesContext;
